// public/js/auth/AuthUI.js
import overlay from '@services/overlay.js';

/**
 * AuthUI handles all UI-related functionality for authentication
 * including forms, overlays, and UI state management
 * @class AuthUI
 */
export default class AuthUI {
  /**
   * @constructor
   * @param {Object} authController - Reference to main auth controller
   */
  constructor(authController) {
    this.auth = authController;
    this.isTransitioning = false; // Flag to prevent multiple transitions
    this.activeForm = null; // Track current form
  }

  /**
   * Set up event listeners for auth-related buttons
   */
  async setupEventListeners() {
    const loginButton = document.getElementById("login");
    const logoutButton = document.getElementById("logout");
    const signupButton = document.getElementById("signup"); // Optional signup button
    
    if (loginButton) {
      loginButton.addEventListener("click", (e) => { 
        e.preventDefault();
        this.auth.login();
      });
    }
    
    if (logoutButton) {
      logoutButton.addEventListener("click", (e) => { 
        e.preventDefault();
        this.auth.logout();
      });
    }
    
    // Add event listener for signup button if it exists
    if (signupButton) {
      signupButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.auth.showRegisterOverlay();
      });
    }

    // Add event listener for global overlay events
    document.addEventListener("click", (e) => {
      // Delegate event handling for all auth-related buttons inside overlay
      if (e.target.id === "register-button") {
        e.preventDefault();
        this.handleFormTransition('register');
      } 
      else if (e.target.id === "back-to-login") {
        e.preventDefault();
        this.handleFormTransition('login');
      }
      else if (e.target.id === "forgot-password-link") {
        e.preventDefault();
        this.handleFormTransition('forgot-password');
      }
      else if (e.target.classList.contains("close-overlay-btn")) {
        e.preventDefault();
        overlay.closeOverlay();
      }
    });
  }

  /**
   * Handle transitions between different auth forms
   * @param {string} formType - The type of form to show ('login', 'register', 'forgot-password')
   */
  async handleFormTransition(formType) {
    // Prevent multiple transitions at once
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    
    // Close current overlay
    await overlay.closeOverlay();
    
    // Wait for overlay to close
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Show the requested form
    if (formType === 'login') {
      this.showLoginOverlay();
    } else if (formType === 'register') {
      this.showRegisterOverlay();
    } else if (formType === 'forgot-password') {
      this.showForgotPasswordOverlay();
    }
    
    // Reset transition flag
    this.isTransitioning = false;
  }

/**
 * Update the UI elements based on authentication state
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {string} userName - User's name to display
 */
updateUIState(isAuthenticated, userName) {
  const loginButton = document.getElementById("login");
  const personButton = document.getElementById("person");
  const dashboardButton = document.getElementById("dashboard");
  const signupButton = document.getElementById("signup");
  const logoutButton = document.getElementById("logout");
  const settingsButton = document.getElementById("settings");
  const userNameDisplay = document.getElementById("user_name");

  if (isAuthenticated) {
    if(userNameDisplay) userNameDisplay.innerHTML = userName || 'User';
    if(personButton) personButton.classList.add("show");
    if(dashboardButton) dashboardButton.classList.add("show");
    if(loginButton) loginButton.classList.remove("show");
    if (signupButton) signupButton.classList.remove("show");
    if (logoutButton) logoutButton.classList.add("show");
  } else {
    if(personButton) personButton.classList.remove("show");
    if(dashboardButton) dashboardButton.classList.remove("show");
    if(loginButton) loginButton.classList.add("show");
    if (signupButton) signupButton.classList.add("show");
    if (logoutButton) logoutButton.classList.remove("show");
  }
}

  /**
   * Show login overlay form
   */
  showLoginOverlay() {
    this.activeForm = 'login';
    
    try {
      const loginOverlayContent = `
        <div class="auth-container">
          <button class="close-overlay-btn">&times;</button>
          <h2>Bee On Tour Login</h2>
          
          <div id="login-error" class="error-message" style="display: none;"></div>
          
          <form id="login-form">
            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" name="email" required>
            </div>
            
            <div class="form-group">
              <label for="password">Passwort</label>
              <input type="password" id="password" name="password" required>
            </div>
            
            <div class="form-actions">
              <button type="submit" class="btn-primary">Login</button>
              <button type="button" id="register-button" class="btn-link">Registrieren</button>
            </div>
            
            <div class="form-footer">
              <a href="/" id="forgot-password-link">Passwort vergessen?</a>
            </div>
          </form>
        </div>
      `;
      
      // Show the new overlay
      overlay.showOverlay(loginOverlayContent);
      
      // Set up form-specific event listeners
      const loginForm = document.getElementById('login-form');
      if (loginForm) {
        loginForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          await this._handleLoginSubmit(e);
        });
      }
    } catch (err) {
      console.error("Show login overlay failed", err);
    }
  }

  /**
   * Handle login form submission
   * @private
   */
  async _handleLoginSubmit(e) {
    const email = document.getElementById('email').value;
    const password = document.getElementById('password').value;
    
    try {
      const errorMessage = document.getElementById('login-error');
      errorMessage.style.display = 'none';
      
      // Show loading state
      const submitButton = e.target.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = 'Logging in...';
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include'  // Include cookies for refresh token
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }
      
      // Handle successful login
      await this.auth.handleLoginSuccess(data);
      
      // Close overlay
      overlay.closeOverlay();
    } catch (error) {
      const errorMessage = document.getElementById('login-error');
      errorMessage.textContent = error.message || 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten.';
      errorMessage.style.display = 'block';
      
      // Reset button state
      const submitButton = e.target.querySelector('button[type="submit"]');
      submitButton.disabled = false;
      submitButton.textContent = 'Login';
    }
  }

  /**
   * Show registration overlay form
   */
  showRegisterOverlay() {
    this.activeForm = 'register';
    
    try {
      const registerOverlayContent = `
        <div class="auth-container">
          <button class="close-overlay-btn">&times;</button>
          <h2>Registrieren</h2>
          
          <div id="register-error" class="error-message" style="display: none;"></div>
          <div id="register-success" class="success-message" style="display: none;"></div>
          
          <form id="register-form">
            <div class="form-group">
              <label for="firstName">Vorname</label>
              <input type="text" id="firstName" name="firstName" required>
            </div>
            
            <div class="form-group">
              <label for="lastName">Nachname</label>
              <input type="text" id="lastName" name="lastName" required>
            </div>
            
            <div class="form-group">
              <label for="gender">Geschlecht</label>
              <select id="gender" name="gender">
                <option value="prefer_not_to_say">Keine Angabe</option>
                <option value="male">Männlich</option>
                <option value="female">Weiblich</option>
                <option value="other">Andere</option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" name="email" required>
            </div>
            
            <div class="form-group">
              <label for="password">Passwort</label>
              <input type="password" id="password" name="password" required>
              <div class="form-hint">Das Passwort muss mindestens 8 Zeichen lang sein und mindestens eine Zahl und ein Sonderzeichen enthalten.</div>
            </div>
            
            <div class="form-group">
              <label for="confirmPassword">Passwort bestätigen</label>
              <input type="password" id="confirmPassword" name="confirmPassword" required>
            </div>
            
            <div class="form-actions">
              <button type="submit" class="btn-primary">Konto erstellen</button>
              <button type="button" id="back-to-login" class="btn-link">Bereits registriert?</button>
            </div>
          </form>
        </div>
      `;
      
      // Show the new overlay
      overlay.showOverlay(registerOverlayContent);
      
      // Set up form-specific event listeners
      const registerForm = document.getElementById('register-form');
      if (registerForm) {
        registerForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          await this._handleRegisterSubmit(e);
        });
      }
    } catch (err) {
      console.error("Show register overlay failed", err);
    }
  }

  /**
   * Handle registration form submission
   * @private
   */
  async _handleRegisterSubmit(e) {
    const firstName = document.getElementById('firstName').value;
    const lastName = document.getElementById('lastName').value;
    const gender = document.getElementById('gender').value;
    const email = document.getElementById('email').value;
    const password = document.getElementById('password').value;
    const confirmPassword = document.getElementById('confirmPassword').value;
    
    // Simple validation
    if (password !== confirmPassword) {
      const errorMessage = document.getElementById('register-error');
      errorMessage.textContent = 'Passwörter stimmen nicht überein.';
      errorMessage.style.display = 'block';
      return;
    }
    
    try {
      const errorMessage = document.getElementById('register-error');
      const successMessage = document.getElementById('register-success');
      errorMessage.style.display = 'none';
      successMessage.style.display = 'none';
      
      // Show loading state
      const submitButton = e.target.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = 'Konto wird erstellt...';
      
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName,
          lastName,
          gender,
          email,
          password,
          confirmPassword
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }
      
      // Show success message
      successMessage.textContent = data.message || 'Registrierung erfolgreich. Bitte überprüfen Sie Ihre E-Mail zur Verifizierung.';
      successMessage.style.display = 'block';
      
      // Reset form
      e.target.reset();
      
      // Replace register form with success message
      const registerForm = document.getElementById('register-form');
      registerForm.innerHTML = `
        <div class="form-actions">
          <button type="button" id="back-to-login" class="btn-primary">Zurück zum Login</button>
        </div>
      `;
    } catch (error) {
      const errorMessage = document.getElementById('register-error');
      errorMessage.textContent = error.message || 'Registrierung fehlgeschlagen. Bitte versuchen sie es erneut.';
      errorMessage.style.display = 'block';
      
      // Reset button state
      const submitButton = e.target.querySelector('button[type="submit"]');
      submitButton.disabled = false;
      submitButton.textContent = 'Konto erstellen';
    }
  }

  /**
   * Show forgot password overlay form
   */
  showForgotPasswordOverlay() {
    this.activeForm = 'forgot-password';
    
    try {
      const forgotPasswordOverlayContent = `
        <div class="auth-container">
          <button class="close-overlay-btn">&times;</button>
          <h2>Passwort zurücksetzen</h2>
          
          <div id="reset-error" class="error-message" style="display: none;"></div>
          <div id="reset-success" class="success-message" style="display: none;"></div>
          
          <form id="reset-form">
            <div class="form-group">
              <label for="email">Email</label>
              <input type="email" id="email" name="email" required>
            </div>
            
            <div class="form-actions">
              <button type="submit" class="btn-primary">Link senden</button>
              <button type="button" id="back-to-login" class="btn-link">Zurück zum Login</button>
            </div>
          </form>
        </div>
      `;
      
      // Show the new overlay
      overlay.showOverlay(forgotPasswordOverlayContent);
      
      // Set up form-specific event listeners
      const resetForm = document.getElementById('reset-form');
      if (resetForm) {
        resetForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          await this._handleForgotPasswordSubmit(e);
        });
      }
    } catch (err) {
      console.error("Show forgot password overlay failed", err);
    }
  }

  /**
   * Handle forgot password form submission
   * @private
   */
  async _handleForgotPasswordSubmit(e) {
    const email = document.getElementById('email').value;
    
    try {
      const errorMessage = document.getElementById('reset-error');
      const successMessage = document.getElementById('reset-success');
      errorMessage.style.display = 'none';
      successMessage.style.display = 'none';
      
      // Show loading state
      const submitButton = e.target.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = 'Wird gesendet...';
      
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset link');
      }
      
      // Show success message
      successMessage.textContent = data.message || 'Wenn deine Email-Adresse existiert, hast du eine E-Mail mit einem Link zum Zurücksetzen deines Passworts erhalten.';
      successMessage.style.display = 'block';
      
      // Reset form
      e.target.reset();
      
      // Replace reset form with success message
      const resetForm = document.getElementById('reset-form');
      resetForm.innerHTML = `
        <div class="form-actions">
          <button type="button" id="back-to-login" class="btn-primary">Zurück zum Login</button>
        </div>
      `;
    } catch (error) {
      const errorMessage = document.getElementById('reset-error');
      errorMessage.textContent = error.message || 'Senden des Links fehlgeschlagen. Bitte versuchen Sie es erneut.';
      errorMessage.style.display = 'block';

      // Reset button state
      const submitButton = e.target.querySelector('button[type="submit"]');
      submitButton.disabled = false;
      submitButton.textContent = 'Zurücksetzungslink senden';
    }
  }

  /**
   * Show change password overlay form (for authenticated users)
   */
  showChangePasswordOverlay() {
    this.activeForm = 'change-password';

    try {
      const changePasswordOverlayContent = `
        <div class="auth-container">
          <button class="close-overlay-btn">&times;</button>
          <h2>Passwort ändern</h2>

          <div id="change-password-error" class="error-message" style="display: none;"></div>
          <div id="change-password-success" class="success-message" style="display: none;"></div>

          <form id="change-password-form">
            <div class="form-group">
              <label for="currentPassword">Aktuelles Passwort</label>
              <input type="password" id="currentPassword" name="currentPassword" required>
            </div>

            <div class="form-group">
              <label for="newPassword">Neues Passwort</label>
              <input type="password" id="newPassword" name="newPassword" required minlength="8">
            </div>

            <div class="form-group">
              <label for="confirmPassword">Neues Passwort bestätigen</label>
              <input type="password" id="confirmPassword" name="confirmPassword" required minlength="8">
            </div>

            <div class="form-hint">Das Passwort muss mindestens 8 Zeichen lang sein und mindestens einen Kleinbuchstaben, einen Großbuchstaben, eine Zahl und ein Sonderzeichen enthalten.</div>

            <div class="form-actions">
              <button type="submit" class="btn-primary">Passwort ändern</button>
              <button type="button" class="btn-secondary" id="cancel-password-change">Abbrechen</button>
            </div>
          </form>
        </div>
      `;

      // Show the new overlay
      overlay.showOverlay(changePasswordOverlayContent);

      // Set up form-specific event listeners
      const changePasswordForm = document.getElementById('change-password-form');
      if (changePasswordForm) {
        changePasswordForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          await this._handleChangePasswordSubmit(e);
        });
      }

      // Add cancel button event listener
      const cancelButton = document.getElementById('cancel-password-change');
      if (cancelButton) {
        cancelButton.addEventListener('click', () => {
          overlay.closeOverlay();
        });
      }
    } catch (err) {
      console.error("Show change password overlay failed", err);
    }
  }

  /**
   * Handle change password form submission
   * @private
   */
  async _handleChangePasswordSubmit(e) {
    const currentPassword = document.getElementById('currentPassword').value;
    const newPassword = document.getElementById('newPassword').value;
    const confirmPassword = document.getElementById('confirmPassword').value;

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      const errorMessage = document.getElementById('change-password-error');
      errorMessage.textContent = 'Neue Passwörter stimmen nicht überein.';
      errorMessage.style.display = 'block';
      return;
    }

    // Validate password requirements (must match backend validation)
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[\S]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      const errorMessage = document.getElementById('change-password-error');
      errorMessage.textContent = 'Das Passwort muss mindestens 8 Zeichen lang sein und mindestens einen Kleinbuchstaben, einen Großbuchstaben, eine Zahl und ein Sonderzeichen enthalten.';
      errorMessage.style.display = 'block';
      return;
    }

    try {
      const errorMessage = document.getElementById('change-password-error');
      const successMessage = document.getElementById('change-password-success');
      errorMessage.style.display = 'none';
      successMessage.style.display = 'none';

      // Show loading state
      const submitButton = e.target.querySelector('button[type="submit"]');
      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = 'Wird geändert...';

      // Get access token
      const accessToken = await this.auth.getAccessToken();

      const response = await fetch('/api/auth/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password change failed');
      }

      // Show success message
      successMessage.textContent = data.message || 'Passwort erfolgreich geändert.';
      successMessage.style.display = 'block';

      // Reset form
      e.target.reset();

      // Close overlay after success
      setTimeout(() => {
        overlay.closeOverlay();
      }, 2000);

    } catch (error) {
      const errorMessage = document.getElementById('change-password-error');
      errorMessage.textContent = error.message || 'Passwortänderung fehlgeschlagen. Bitte versuchen Sie es erneut.';
      errorMessage.style.display = 'block';

      // Reset button state
      const submitButton = e.target.querySelector('button[type="submit"]');
      submitButton.disabled = false;
      submitButton.textContent = 'Passwort ändern';
    }
  }
}